'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useLocale, useTranslations } from 'next-intl';
import { useCurrentUser, SigninModal } from '@/contexts/authentication/client';
import { Button } from '@/shared/components/ui/button';
import {
  Sparkles,
  Mic,
  BookOpen,
  Printer,
  Video,
  Calendar,
  Globe,
  Heart,
  ArrowRight,
  Wand2,
  ImageIcon,
  Gamepad2,
  Clapperboard,
} from 'lucide-react';

/**
 * About page client component.
 *
 * Displays the marketing/information page about the platform.
 * Moved from app/ route to context component per project standard
 * (all client components live in contexts/).
 */
export default function AboutClient() {
  const t = useTranslations('about');
  const locale = useLocale();
  const { isAuthenticated } = useCurrentUser();
  const [isSigninModalOpen, setIsSigninModalOpen] = useState(false);

  const storyFeatures = [
    { icon: Wand2, key: 'aiStories', gradient: 'from-brand-blue to-brand-purple' },
    { icon: Mic, key: 'voiceCloning', gradient: 'from-brand-purple to-brand-pink' },
    { icon: ImageIcon, key: 'illustrations', gradient: 'from-brand-pink to-brand-orange' },
    { icon: Printer, key: 'printedBooks', gradient: 'from-brand-orange to-warning' },
    { icon: Video, key: 'videoExport', gradient: 'from-success to-brand-blue' },
    { icon: BookOpen, key: 'characters', gradient: 'from-brand-blue to-success' },
  ];

  // Sibling OwlCat apps — each on its own subdomain of the fleet parent domain; hidden when
  // the parent domain is not configured (bare-localhost dev).
  const parent = process.env.NEXT_PUBLIC_OWLCAT_PARENT_DOMAIN;
  const beyondStories = [
    { icon: Calendar, key: 'habits', gradient: 'from-success to-brand-green', href: parent ? `https://habits.${parent}/${locale}` : null },
    { icon: Gamepad2, key: 'games', gradient: 'from-brand-blue to-brand-purple', href: parent ? `https://games.${parent}/${locale}` : null },
    { icon: Clapperboard, key: 'film', gradient: 'from-brand-pink to-brand-orange', href: parent ? `https://film.${parent}/${locale}` : null },
  ];

  const safetyFeatures = [
    { icon: Globe, key: 'privacy' },
    { icon: Heart, key: 'ageAppropriate' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted">
      {/* Hero */}
      <section className="relative overflow-hidden py-20 lg:py-32">
        <div className="absolute inset-0">
          <div className="absolute top-20 left-[10%] w-72 h-72 bg-brand-blue/8 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-[10%] w-96 h-96 bg-brand-purple/8 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-8">
              <Sparkles className="w-4 h-4" />
              {t('hero.badge')}
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground leading-[1.1] mb-6">
              <span className="block">{t('hero.title')}</span>
              <span className="block mt-2 bg-gradient-to-r from-brand-blue via-brand-purple to-brand-pink bg-clip-text text-transparent">
                {t('hero.titleAccent')}
              </span>
            </h1>

            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              {t('hero.description')}
            </p>
          </div>
        </div>
      </section>

      {/* How a Story is Born */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {t('storyPipeline.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('storyPipeline.subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {storyFeatures.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.key}
                  className="bg-card rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 bg-gradient-to-br ${feature.gradient} rounded-xl flex items-center justify-center shadow-md shrink-0 group-hover:scale-110 transition-transform`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="text-xs font-bold text-muted-foreground mb-1">
                        {String(idx + 1).padStart(2, '0')}
                      </div>
                      <h3 className="text-lg font-bold text-foreground mb-2">
                        {t(`storyPipeline.features.${feature.key}.title`)}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {t(`storyPipeline.features.${feature.key}.description`)}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Beyond Stories */}
      <section className="py-16 lg:py-24 bg-card/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
              {t('beyond.title')}
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              {t('beyond.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {beyondStories.map((feature) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.key}
                  className="bg-card rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                  <div className={`w-14 h-14 bg-gradient-to-br ${feature.gradient} rounded-2xl flex items-center justify-center shadow-lg mb-6 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-3">
                    {t(`beyond.features.${feature.key}.title`)}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed mb-4">
                    {t(`beyond.features.${feature.key}.description`)}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[1, 2, 3].map((i) => (
                      <span
                        key={i}
                        className="text-xs font-medium px-3 py-1 rounded-full bg-muted text-foreground/80"
                      >
                        {t(`beyond.features.${feature.key}.h${i}`)}
                      </span>
                    ))}
                  </div>
                  {feature.href && (
                    <a
                      href={feature.href}
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                      data-testid={`about-app-${feature.key}`}
                    >
                      {t('beyond.open')}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Safety & Parental Control */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-card rounded-3xl shadow-2xl p-8 lg:p-16">
            <div className="text-center mb-12">
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                {t('safety.title')}
              </h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                {t('safety.subtitle')}
              </p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {safetyFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <div key={feature.key} className="text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-success to-brand-green rounded-full text-white mb-4">
                      <Icon className="w-6 h-6" />
                    </div>
                    <h3 className="text-base font-bold text-foreground mb-2">
                      {t(`safety.features.${feature.key}.title`)}
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      {t(`safety.features.${feature.key}.description`)}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-r from-brand-blue via-brand-purple to-brand-pink rounded-3xl shadow-2xl p-8 lg:p-16">
            <div className="grid sm:grid-cols-4 gap-8 text-center text-white">
              <div>
                <div className="text-4xl lg:text-5xl font-bold mb-2">{t('stats.languageCount')}</div>
                <div className="text-sm lg:text-base opacity-90">{t('stats.languages')}</div>
              </div>
              <div>
                <div className="text-4xl lg:text-5xl font-bold mb-2">{'\u221E'}</div>
                <div className="text-sm lg:text-base opacity-90">{t('stats.stories')}</div>
              </div>
              <div>
                <div className="text-4xl lg:text-5xl font-bold mb-2">{'\uD83C\uDFA4'}</div>
                <div className="text-sm lg:text-base opacity-90">{t('stats.voiceCloning')}</div>
              </div>
              <div>
                <div className="text-4xl lg:text-5xl font-bold mb-2">{'\uD83D\uDCDA'}</div>
                <div className="text-sm lg:text-base opacity-90">{t('stats.printedBooks')}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
            {t('cta.title')}
          </h2>
          <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
            {t('cta.description')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            {isAuthenticated ? (
              <Button size="lg" variant="gradient-blue" className="shadow-xl text-lg px-8" asChild>
                <Link href={`/${locale}/create`}>
                  <Sparkles className="h-5 w-5" />
                  {t('cta.createStory')}
                </Link>
              </Button>
            ) : (
              <Button
                size="lg"
                variant="gradient-blue"
                onClick={() => setIsSigninModalOpen(true)}
                className="shadow-xl text-lg px-8"
              >
                <Sparkles className="h-5 w-5" />
                {t('cta.getStarted')}
              </Button>
            )}
            <Button variant="outline" size="lg" className="text-lg px-8" asChild>
              <Link href={`/${locale}`}>
                {t('cta.backToHome')}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      <SigninModal
        isOpen={isSigninModalOpen}
        onClose={() => setIsSigninModalOpen(false)}
      />
    </div>
  );
}
